import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import api from "../../axiosInstance";
import { toast } from "react-toastify";

const ACCENT = "#17479e";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`;

function MentorSessionLimitDialog({ open, mentorRow, onClose, onSuccess }) {
  const [sessionTypes, setSessionTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [sessionTypeId, setSessionTypeId] = useState("");
  const [minSessionsPerMonth, setMinSessionsPerMonth] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSessionTypeId("");
    setMinSessionsPerMonth("");
    setLoadingTypes(true);
    api
      .get("/admin/mentor-session-types")
      .then((res) => {
        const raw = res.data?.data;
        setSessionTypes(Array.isArray(raw) ? raw : (raw?.content ?? []));
      })
      .catch((err) => {
        console.error("Session types fetch error:", err);
        toast.error("Failed to load session types.");
      })
      .finally(() => setLoadingTypes(false));
  }, [open]);

  const handleSubmit = async () => {
    if (!mentorRow || !sessionTypeId || minSessionsPerMonth === "") return;
    setSaving(true);
    try {
      await api.put(`/admin/mentors/${mentorRow.mentorId}/quota`, {
        minSessionsPerMonth: Number(minSessionsPerMonth),
        sessionTypeId: Number(sessionTypeId),
      });
      toast.success("Session limit updated successfully!");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to update session limit. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          background: HEADER_GRADIENT,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              background: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TuneIcon sx={{ color: "#fff", fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{ color: "#fff", fontWeight: 700, fontSize: "1.05rem" }}
            >
              Change Session Limit
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem" }}
            >
              {mentorRow?.mentorName ?? "—"}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ color: "#fff", fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
          <InputLabel>Session Type</InputLabel>
          {loadingTypes ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Select
              label="Session Type"
              value={sessionTypeId}
              onChange={(e) => setSessionTypeId(e.target.value)}
            >
              {sessionTypes.map((st) => (
                <MenuItem key={st.id} value={st.id}>
                  {st.name}
                </MenuItem>
              ))}
            </Select>
          )}
        </FormControl>

        <TextField
          fullWidth
          size="small"
          type="number"
          label="Min Sessions / Month"
          placeholder="e.g. 2"
          value={minSessionsPerMonth}
          onChange={(e) => setMinSessionsPerMonth(e.target.value)}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            color: "#616161",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || !sessionTypeId || minSessionsPerMonth === ""}
          onClick={handleSubmit}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            color: "#fff !important",
            fontWeight: 700,
            px: 3,
            background: HEADER_GRADIENT,
            boxShadow: "none",
          }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MentorSessionLimitDialog;
